import { useSoulMapDocumentDownload } from '@/hooks/useSoulMapDocumentDownload';
import { useSoulMapDocumentRetry } from '@/hooks/useSoulMapDocumentRetry';
import { useSoulMapDocuments } from '@/hooks/useSoulMapDocuments';
import {
  getFullSoulMapDocument,
  getSoulMapHeaderState,
  getSoulMapPreparingLabel,
  hasBirthProfileRecomputeError,
} from '@/utils/soulmap-document-view';
import {
  CircleCheckBig,
  Download,
  LoaderCircle,
  RefreshCw,
} from 'lucide-react';

/** Full width below the title on mobile, an inline pill on desktop. */
const PILL_BASE =
  'order-3 mt-2 flex w-full min-w-0 items-center rounded-xl border px-2.5 py-2 text-xs font-medium md:order-none md:mt-0 md:w-auto md:rounded-full';

/**
 * Soul Map document pill in the chat header, from the LiFT chat page design:
 * ready with the Full Soul Map download, preparing while the job runs, and
 * hidden for every other status.
 */
export function SoulMapDocumentControls({
  practiceId,
  statusQuery,
  treatExpiredAsPreparing = false,
  regenerateExpired = false,
  onReviewBirthDetails,
}: {
  practiceId: string;
  statusQuery: ReturnType<typeof useSoulMapDocuments>;
  treatExpiredAsPreparing?: boolean;
  regenerateExpired?: boolean;
  onReviewBirthDetails?: () => void;
}) {
  const { data } = statusQuery;
  const headerState = getSoulMapHeaderState(data, {
    treatExpiredAsPreparing,
    regenerateExpired,
  });
  const pdf = getFullSoulMapDocument(data);
  const { download, isDownloading } = useSoulMapDocumentDownload(
    practiceId,
    statusQuery
  );
  const { retry, canRetry, isRetrying } = useSoulMapDocumentRetry(
    practiceId,
    statusQuery
  );

  if (headerState === 'needs-review') {
    return (
      <div
        className={`${PILL_BASE} justify-between gap-2 border-amber-200 bg-amber-50 text-amber-800`}
        data-generation-guard-exempt
      >
        <span role="status">
          {hasBirthProfileRecomputeError(data)
            ? 'We need your birth details again.'
            : 'Needs confirmation'}
        </span>
        {onReviewBirthDetails && (
          <button
            type="button"
            onClick={onReviewBirthDetails}
            className="flex items-center gap-1 whitespace-nowrap rounded-full bg-white px-2.5 py-1.5 text-[11px] font-semibold text-amber-800 shadow-sm transition hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] md:text-xs"
          >
            Review and regenerate
          </button>
        )}
      </div>
    );
  }

  if (headerState === 'failed') {
    return (
      <div
        className={`${PILL_BASE} justify-between gap-2 border-red-200 bg-red-50 text-red-700`}
        data-generation-guard-exempt
      >
        <span role="status">
          {canRetry
            ? 'Your Soul Map needs another try.'
            : 'We couldn’t prepare your Soul Map.'}
        </span>

        {canRetry && (
          <button
            type="button"
            onClick={retry}
            disabled={isRetrying}
            aria-busy={isRetrying}
            className="flex items-center gap-1 whitespace-nowrap rounded-full bg-white px-2.5 py-1.5 text-[11px] font-semibold text-red-700 shadow-sm transition hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] disabled:cursor-wait disabled:opacity-70 md:text-xs"
          >
            <RefreshCw
              size={13}
              className={isRetrying ? 'animate-spin' : ''}
              aria-hidden="true"
            />
            {isRetrying ? 'Retrying…' : 'Try again'}
          </button>
        )}
      </div>
    );
  }

  if (headerState === 'preparing') {
    return (
      <div
        role="status"
        className={`${PILL_BASE} whitespace-nowrap border-gray-200 bg-gray-50 text-gray-600`}
        data-generation-guard-exempt
      >
        <span>{getSoulMapPreparingLabel(data)}</span>
      </div>
    );
  }

  if (headerState !== 'ready' || !pdf) {
    return null;
  }

  return (
    <div
      className={`${PILL_BASE} justify-between gap-2 border-success-200 bg-success-50 text-success-700`}
      data-generation-guard-exempt
    >
      <div
        role="status"
        className="flex items-center gap-1.5 whitespace-nowrap"
      >
        <CircleCheckBig
          size={15}
          className="flex-shrink-0"
          aria-hidden="true"
        />
        <span>
          {pdf.downloadCount > 0 ? 'Soul Map ready' : 'New Soul Map ready'}
        </span>
      </div>
      <button
        type="button"
        onClick={() => void download(pdf.url)}
        disabled={isDownloading}
        aria-busy={isDownloading}
        className="flex items-center gap-1 whitespace-nowrap rounded-full bg-white px-2.5 py-1.5 text-[11px] font-semibold text-primary-600 shadow-sm transition hover:text-primary-700 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] disabled:cursor-wait disabled:opacity-70 md:text-xs"
      >
        {isDownloading ? (
          <LoaderCircle size={13} className="animate-spin" aria-hidden="true" />
        ) : (
          <Download size={13} aria-hidden="true" />
        )}
        Full Soul Map
      </button>
    </div>
  );
}
